import type { ScanBackend, ScanError } from './scanner';

/** Maps a getUserMedia / qr-scanner failure to the scanner error kinds. */
export function webScanErrorReason(error: unknown): ScanError {
  const name = error instanceof DOMException ? error.name : '';
  const message = error instanceof Error ? error.message : String(error);
  if (
    name === 'NotAllowedError' ||
    name === 'SecurityError' ||
    /denied|permission/i.test(message)
  ) {
    return 'permission-denied';
  }
  return 'unavailable';
}

/**
 * Browser backend: the `qr-scanner` library (native BarcodeDetector when the
 * browser has it, a WebAssembly decoder in a worker otherwise) on a <video>.
 */
export function createWebScanBackend(video: HTMLVideoElement): ScanBackend {
  return async ({ onText, onError }) => {
    const { default: QrScanner } = await import('qr-scanner');
    const scanner = new QrScanner(
      video,
      (result) => {
        if (result.data) onText(result.data);
      },
      {
        preferredCamera: 'environment',
        returnDetailedScanResult: true,
        highlightScanRegion: false,
        highlightCodeOutline: false,
      },
    );
    try {
      await scanner.start();
    } catch (error: unknown) {
      scanner.destroy();
      onError(webScanErrorReason(error));
      return () => undefined;
    }
    return () => {
      scanner.stop();
      scanner.destroy();
    };
  };
}
